import { useEffect, useState } from 'react';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Search } from 'lucide-react';
import { adminSupportOrgsApi } from '../../lib/api';

/**
 * Reusable fieldset rendered inside Admin Document Types + Library Tags
 * editor dialogs. Provides:
 *   - "Emotional safety" toggle (emotional_safety_enabled)
 *   - Multi-select of support organisations (support_org_ids)
 *
 * Props:
 *   editing       — current dialog state
 *   setEditing    — setter from the dialog
 *   testIdPrefix  — for data-testid scoping
 */
export const SafetyFieldset = ({ editing, setEditing, testIdPrefix }) => {
  const enabled = !!editing?.emotional_safety_enabled;
  const selectedIds = editing?.support_org_ids || [];

  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminSupportOrgsApi
      .list()
      .then((res) => { if (!cancelled) setOrgs(res.data?.results || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggleOrg = (id) => {
    setEditing((p) => {
      const cur = p.support_org_ids || [];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...p, support_org_ids: next };
    });
  };

  const filteredOrgs = orgs.filter((o) => {
    if (o.active === false) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (o.name || '').toLowerCase().includes(needle) ||
      (o.category || '').toLowerCase().includes(needle) ||
      (o.description || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-4 rounded-lg border border-rule p-4" data-testid={`${testIdPrefix}-safety-fieldset`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm font-medium">Emotional safety</Label>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                When enabled, records tagged here show a gentle support banner and a
                "gentle return" prompt after close. Select which support orgs to suggest below.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => setEditing((p) => ({ ...p, emotional_safety_enabled: !!v }))}
              data-testid={`${testIdPrefix}-safety-toggle`}
            />
          </div>
        </div>
      </div>

      {enabled && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Support organisations to suggest
            </Label>
            <span className="text-[11px] text-muted-foreground">
              {selectedIds.length} selected
            </span>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or category…"
              className="pl-9 h-9 text-sm"
              data-testid={`${testIdPrefix}-safety-search`}
            />
          </div>
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading support orgs…</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {filteredOrgs.map((o) => (
                <label
                  key={o.id}
                  className="flex items-start gap-2 text-sm cursor-pointer rounded-md hover:bg-muted/40 px-2 py-1.5"
                >
                  <Checkbox
                    checked={selectedIds.includes(o.id)}
                    onCheckedChange={() => toggleOrg(o.id)}
                    data-testid={`${testIdPrefix}-safety-org-${o.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{o.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{o.category}</Badge>
                      {o.phone && <span className="text-xs text-muted-foreground">· {o.phone}</span>}
                    </div>
                    {o.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{o.description}</p>
                    )}
                  </div>
                </label>
              ))}
              {filteredOrgs.length === 0 && (
                <p className="text-xs text-muted-foreground italic px-2">No matches.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SafetyFieldset;
